import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Container,
  Form,
  InputGroup,
  ListGroup,
  Table,
} from 'react-bootstrap';
import { DashCircle, PlusCircle, Search, Trash } from 'react-bootstrap-icons';
import { Navigate } from 'react-router-dom';

import { UserContext } from '../../hooks/useUser';
import { Paths } from '../../pages';
import { AdminRum, InventoryRun, InventoryRunItem } from '../../types';
import LoadingScreen from '../LoadingScreen';
import FinalizeConfirmModal from './FinalizeConfirmModal';
import EditInventoryConfirmModal from './EditInventoryConfirmModal';
import InventoryReports from './InventoryReports';
import { formatRunLabel, isRunActive, sortInventoryRuns } from './inventoryUtils';
import { useInventoryApi } from './useInventoryApi';

/** Extract a comparable run id (API may return URL, number, or nested object). */
function toRunId(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'object' && 'id' in value) {
    return String((value as { id: unknown }).id ?? '');
  }
  const s = String(value);
  // Handle URL-style references e.g. "https://api/.../inventory-runs/42/" -> "42"
  if (s.includes('/')) {
    const segment = s.replace(/\/+$/, '').split('/').pop() ?? '';
    return segment;
  }
  return s;
}

/** Normalize so inventory_run is always a string id (API may return inventory_id or nested object). */
function normalizeInventoryRunItem(
  item: InventoryRunItem & { inventory_id?: string; inventory_run?: string | { id?: string } },
  runId?: string,
): InventoryRunItem {
  const raw =
    item.inventory_run ?? (item as { inventory_id?: unknown }).inventory_id ?? runId ?? '';
  const inventory_run = typeof raw === 'object' && raw !== null ? toRunId(raw) : toRunId(raw);
  return { ...item, inventory_run };
}

function InventoryPage(): JSX.Element {
  const { authToken } = useContext(UserContext);

  const [isLoaded, setIsLoaded] = useState(false);
  const [rums, setRums] = useState<AdminRum[]>([]);
  const [inventoryRuns, setInventoryRuns] = useState<InventoryRun[]>([]);
  const [inventoryRunItems, setInventoryRunItems] = useState<InventoryRunItem[]>([]);
  const [selectedRunId, setSelectedRunId] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRumId, setSelectedRumId] = useState<number | null>(null);
  const [showFinalizeModal, setShowFinalizeModal] = useState(false);
  const [showEditInventoryModal, setShowEditInventoryModal] = useState(false);
  const [pendingEditFinalizedRunId, setPendingEditFinalizedRunId] = useState<string | null>(null);
  const [editingFinalizedRunId, setEditingFinalizedRunId] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const hasStartedLoading = useRef(false);
  const api = useInventoryApi();

  // --- Data loading ---

  useEffect(() => {
    if (!authToken || hasStartedLoading.current) return;
    hasStartedLoading.current = true;

    const loadData = async (): Promise<void> => {
      try {
        const [rumsData, runsData, itemsData] = await Promise.all([
          api.fetchRums(),
          api.fetchInventoryRuns(),
          api.fetchInventoryRunItems(),
        ]);
        setRums(rumsData);
        setInventoryRuns(runsData);
        setInventoryRunItems(itemsData.map(item => normalizeInventoryRunItem(item)));

        if (runsData.length > 0) {
          const active = runsData.find(r => isRunActive(r));
          const runToSelect = active ?? runsData[0];
          setSelectedRunId(String(toRunId(runToSelect.id)));
        }

        setIsLoaded(true);
      } catch (err) {
        console.error('Failed to load inventory data:', err);
        setLoadError('Failed to load inventory data. Please refresh the page.');
      }
    };

    loadData();
  }, [authToken, api]);

  // --- Derived state ---

  const sortedRuns = useMemo(() => sortInventoryRuns(inventoryRuns), [inventoryRuns]);

  const activeRun = useMemo(
    () => inventoryRuns.find(r => isRunActive(r)) ?? null,
    [inventoryRuns],
  );

  const selectedRun = useMemo(
    () =>
      inventoryRuns.find(r => toRunId(r.id) === toRunId(selectedRunId)) ?? null,
    [inventoryRuns, selectedRunId],
  );

  const isSelectedRunActive = selectedRun != null && isRunActive(selectedRun);
  const isSelectedRunFinalized = selectedRun != null && !!selectedRun.end_date;
  const isEditingFinalizedRun =
    selectedRun != null && editingFinalizedRunId != null && toRunId(selectedRun.id) === toRunId(editingFinalizedRunId);

  const currentRunItems = useMemo(
    () =>
      inventoryRunItems.filter(
        item => toRunId(item.inventory_run) === toRunId(selectedRunId),
      ),
    [inventoryRunItems, selectedRunId],
  );

  const rumLookup = useMemo(() => {
    const map = new Map<number, AdminRum>();
    rums.forEach(r => map.set(r.id, r));
    return map;
  }, [rums]);

  const sortedCurrentRunItems = useMemo(
    () => [...currentRunItems].sort((a, b) => {
      const rumA = rumLookup.get(Number(a.rum));
      const rumB = rumLookup.get(Number(b.rum));
      const idA = rumA?.inventory_id ?? '';
      const idB = rumB?.inventory_id ?? '';
      return idA.localeCompare(idB, undefined, { numeric: true });
    }),
    [currentRunItems, rumLookup],
  );

  const searchResults = useMemo(() => {
    const q = searchQuery.trim();
    if (!q) return [];
    return rums.filter(rum => rum.inventory_id != null && rum.inventory_id.includes(q));
  }, [rums, searchQuery]);

  const selectedRum = useMemo(
    () => (selectedRumId != null ? rumLookup.get(selectedRumId) ?? null : null),
    [selectedRumId, rumLookup],
  );

  const selectedRumRunItem = useMemo(() => {
    if (selectedRumId == null) return null;
    return (
      currentRunItems.find(
        item =>
          item.rum != null &&
          (String(item.rum) === String(selectedRumId) ||
            Number(item.rum) === Number(selectedRumId)),
      ) ?? null
    );
  }, [currentRunItems, selectedRumId]);

  // --- Actions ---

  const handleStartInventory = useCallback(async () => {
    try {
      const newRun = await api.createInventoryRun();
      setInventoryRuns(prev => [...prev, newRun]);
      setSelectedRunId(String(toRunId(newRun.id)));
    } catch (err) {
      console.error('Failed to start inventory run:', err);
      alert('Failed to start inventory run.');
    }
  }, [api]);

  const handleFinalizeRun = useCallback(async () => {
    if (!selectedRun) return;
    try {
      const updated = await api.updateInventoryRun({
        ...selectedRun,
        end_date: new Date().toISOString(),
      });
      setInventoryRuns(prev => prev.map(r => (r.id === updated.id ? updated : r)));
      setShowFinalizeModal(false);
      setSearchQuery('');
      setSelectedRumId(null);
    } catch (err) {
      console.error('Failed to finalize inventory run:', err);
      alert('Failed to finalize inventory run.');
    }
  }, [api, selectedRun]);

  const handleConfirmEditFinalizedRun = useCallback(() => {
    if (!pendingEditFinalizedRunId) return;
    setEditingFinalizedRunId(pendingEditFinalizedRunId);
    setShowEditInventoryModal(false);
    setPendingEditFinalizedRunId(null);
    setSearchQuery('');
    setSelectedRumId(null);
  }, [pendingEditFinalizedRunId]);

  const handleCancelEditFinalizedRun = useCallback(() => {
    setShowEditInventoryModal(false);
    setPendingEditFinalizedRunId(null);
  }, []);

  const handleFinishEditingFinalizedRun = useCallback(() => {
    setEditingFinalizedRunId(null);
    setSearchQuery('');
    setSelectedRumId(null);
  }, []);

  const handleAddRumToInventory = useCallback(async () => {
    if (!selectedRun || selectedRumId == null) return;
    if (selectedRumRunItem) return; // already in this run — use count/remove instead
    try {
      const newItem = await api.createInventoryRunItem({
        inventory_run: selectedRun.id,
        rum: String(selectedRumId),
        count: 1,
      });
      // Build the item we need for UI; API may return different shape (nested run, etc.)
      const itemForState: InventoryRunItem = {
        id: newItem.id,
        inventory_run: selectedRun.id,
        rum: String(selectedRumId),
        count: typeof newItem.count === 'number' ? newItem.count : 1,
      };
      setInventoryRunItems(prev => [...prev, itemForState]);
    } catch (err) {
      console.error('Failed to add rum to inventory:', err);
      alert('Failed to add rum to inventory.');
    }
  }, [api, selectedRun, selectedRumId, selectedRumRunItem]);

  const handleUpdateCount = useCallback(async (item: InventoryRunItem, delta: number) => {
    const newCount = Math.max(0, item.count + delta);
    if (newCount === item.count) return;

    const optimistic = { ...item, count: newCount };
    setInventoryRunItems(prev => prev.map(i => (i.id === item.id ? optimistic : i)));

    try {
      const serverItem = await api.updateInventoryRunItem(optimistic);
      setInventoryRunItems(prev =>
        prev.map(i =>
          i.id === serverItem.id
            ? normalizeInventoryRunItem(serverItem, item.inventory_run)
            : i,
        ),
      );
    } catch (err) {
      console.error('Failed to update count:', err);
      setInventoryRunItems(prev => prev.map(i => (i.id === item.id ? item : i)));
      alert('Failed to update count.');
    }
  }, [api]);

  const handleRemoveFromInventory = useCallback(async (item: InventoryRunItem) => {
    try {
      await api.deleteInventoryRunItem(item.id);
      setInventoryRunItems(prev => prev.filter(i => i.id !== item.id));
    } catch (err) {
      console.error('Failed to remove rum from inventory:', err);
      alert('Failed to remove rum from inventory.');
    }
  }, [api]);

  const handleSelectRunItem = useCallback((rum: AdminRum) => {
    setSelectedRumId(rum.id);
    // Clear so the next inventory ID is easy to type after selecting.
    setSearchQuery('');
  }, []);

  // --- Auth guard ---

  if (!authToken) {
    return <Navigate to={Paths.root} replace />;
  }

  if (loadError) {
    return (
      <Container fluid data-bs-theme="dark" data-theme="dark" className="text-light" id="body">
        <Alert variant="danger" className="mt-3">{loadError}</Alert>
      </Container>
    );
  }

  if (!isLoaded) {
    return <LoadingScreen />;
  }

  // --- Render ---

  return (
    <Container fluid data-bs-theme="dark" data-theme="dark" className="text-light" id="body">
      <h1 className="mb-3">Inventory</h1>

      {/* Start Inventory — only when no active run */}
      {!activeRun && (
        <Button variant="success" className="mb-3" onClick={handleStartInventory}>
          Start Inventory
        </Button>
      )}

      {/* Run selector dropdown — only when there is at least one run */}
      {sortedRuns.length > 0 && (
        <Form.Group className="mb-3">
          <Form.Label>Inventory Run</Form.Label>
          <Form.Select
            value={selectedRunId}
            onChange={e => {
              setSelectedRunId(e.target.value);
              // Prevent stale edit mode if the selected run changes.
              setEditingFinalizedRunId(null);
              setPendingEditFinalizedRunId(null);
              setShowEditInventoryModal(false);
              setSearchQuery('');
              setSelectedRumId(null);
            }}
          >
            {sortedRuns.map(run => (
              <option key={String(toRunId(run.id))} value={String(toRunId(run.id))}>
                {formatRunLabel(run)}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      )}

      {/* ===== ACTIVE RUN EDITOR / FINALIZED EDITOR ===== */}
      {selectedRun && (isSelectedRunActive || isEditingFinalizedRun) && (
        <>
          {/* Rum search */}
          <Form.Group className="mb-3">
            <Form.Label>Search by Inventory ID</Form.Label>
            <InputGroup>
              <InputGroup.Text><Search /></InputGroup.Text>
              <Form.Control
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="Enter inventory ID number…"
                value={searchQuery}
                onChange={e => {
                  setSearchQuery(e.target.value.replace(/[^0-9]/g, ''));
                  setSelectedRumId(null);
                }}
              />
            </InputGroup>
          </Form.Group>

          {/* Search results list */}
          {searchQuery && selectedRumId == null && searchResults.length > 0 && (
            <ListGroup className="mb-3">
              {searchResults.slice(0, 20).map(rum => (
                <ListGroup.Item
                  key={rum.id}
                  action
                  variant="dark"
                  onClick={() => {
                    setSelectedRumId(rum.id);
                    // Clear so the next inventory ID is easy to type.
                    setSearchQuery('');
                  }}
                >
                  <strong>{rum.inventory_id}</strong> — {rum.bevager_name}
                </ListGroup.Item>
              ))}
              {searchResults.length > 20 && (
                <ListGroup.Item variant="dark" className="text-muted text-center">
                  {searchResults.length - 20} more results — keep typing to narrow down
                </ListGroup.Item>
              )}
            </ListGroup>
          )}

          {searchQuery && selectedRumId == null && searchResults.length === 0 && (
            <Alert variant="secondary" className="mb-3">
              No rums found matching &ldquo;{searchQuery}&rdquo;
            </Alert>
          )}

          {/* Selected rum panel */}
          {selectedRum && (
            <div className="border border-secondary rounded p-3 mb-3">
              <h5 className="mb-2">
                {selectedRum.inventory_id} — {selectedRum.bevager_name}
              </h5>

              {selectedRumRunItem ? (
                <div className="d-flex align-items-center flex-wrap gap-2 mt-2">
                  <span className="me-1">Count:</span>
                  <Button
                    variant="outline-light"
                    size="sm"
                    onClick={() => handleUpdateCount(selectedRumRunItem, -1)}
                    disabled={selectedRumRunItem.count <= 0}
                  >
                    <DashCircle />
                  </Button>
                  <span className="fs-4 fw-bold mx-1">{selectedRumRunItem.count}</span>
                  <Button
                    variant="outline-light"
                    size="sm"
                    onClick={() => handleUpdateCount(selectedRumRunItem, 1)}
                  >
                    <PlusCircle />
                  </Button>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    className="ms-auto"
                    onClick={() => handleRemoveFromInventory(selectedRumRunItem)}
                  >
                    <Trash className="me-1" /> Remove
                  </Button>
                </div>
              ) : (
                <Button variant="primary" className="mt-1" onClick={handleAddRumToInventory}>
                  Add to Inventory
                </Button>
              )}
            </div>
          )}

          {/* Items currently in this run */}
          {sortedCurrentRunItems.length > 0 && (
            <>
              <h4 className="mt-4 mb-2">
                Items in This Run ({sortedCurrentRunItems.length})
              </h4>
              <Table striped bordered hover variant="dark" size="sm">
                <thead>
                  <tr>
                    <th>Inventory ID</th>
                    <th>Name</th>
                    <th>Count</th>
                    <th style={{ width: '140px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedCurrentRunItems.map(item => {
                    const rum = rumLookup.get(Number(item.rum));
                    return (
                      <tr
                        key={item.id}
                        onClick={() => rum && handleSelectRunItem(rum)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td>{rum?.inventory_id ?? '—'}</td>
                        <td>{rum?.bevager_name ?? 'Unknown'}</td>
                        <td>{item.count}</td>
                        <td onClick={e => e.stopPropagation()}>
                          <Button
                            variant="outline-light"
                            size="sm"
                            className="me-1"
                            onClick={() => handleUpdateCount(item, -1)}
                            disabled={item.count <= 0}
                          >
                            <DashCircle />
                          </Button>
                          <Button
                            variant="outline-light"
                            size="sm"
                            className="me-1"
                            onClick={() => handleUpdateCount(item, 1)}
                          >
                            <PlusCircle />
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleRemoveFromInventory(item)}
                          >
                            <Trash />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </>
          )}

          {/* Finalize button */}
          <div className="mt-3 mb-4">
            {isSelectedRunActive ? (
              <Button variant="warning" onClick={() => setShowFinalizeModal(true)}>
                Finalize Inventory Run
              </Button>
            ) : (
              <Button variant="secondary" onClick={handleFinishEditingFinalizedRun}>
                Finish editing
              </Button>
            )}
          </div>

          <FinalizeConfirmModal
            show={showFinalizeModal}
            onConfirm={handleFinalizeRun}
            onCancel={() => setShowFinalizeModal(false)}
          />
        </>
      )}

      {/* ===== FINALIZED RUN VIEWER ===== */}
      {selectedRun && isSelectedRunFinalized && !isEditingFinalizedRun && (
        <>
          <Alert variant="info" className="mt-2">
            <div>
              This inventory run was completed on{' '}
              {selectedRun.end_date
                ? new Date(selectedRun.end_date).toLocaleDateString()
                : 'N/A'}.
            </div>
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-2">
              <span>Items are read-only.</span>
              <Button
                variant="outline-light"
                size="sm"
                onClick={() => {
                  setPendingEditFinalizedRunId(selectedRunId);
                  setShowEditInventoryModal(true);
                }}
              >
                Edit this Inventory
              </Button>
            </div>
          </Alert>

          {sortedCurrentRunItems.length > 0 ? (
            <Table striped bordered hover variant="dark" size="sm">
              <thead>
                <tr>
                  <th>Inventory ID</th>
                  <th>Name</th>
                  <th>Count</th>
                </tr>
              </thead>
              <tbody>
                {sortedCurrentRunItems.map(item => {
                  const rum = rumLookup.get(Number(item.rum));
                  return (
                    <tr key={item.id}>
                      <td>{rum?.inventory_id ?? '—'}</td>
                      <td>{rum?.bevager_name ?? 'Unknown'}</td>
                      <td>{item.count}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          ) : (
            <p className="text-muted">No items were recorded in this run.</p>
          )}

          <h4 className="mt-4">Reports</h4>
          <InventoryReports
            rums={rums}
            runItems={currentRunItems}
            allRunItems={inventoryRunItems}
            inventoryRuns={inventoryRuns}
            currentRunId={selectedRunId}
            finalizedEndDate={selectedRun.end_date ?? null}
          />
        </>
      )}

      <EditInventoryConfirmModal
        show={showEditInventoryModal}
        onConfirm={handleConfirmEditFinalizedRun}
        onCancel={handleCancelEditFinalizedRun}
      />
    </Container>
  );
}

export { InventoryPage };
